<template>
  <div v-if="editForm" class="hyphen-signature-input-dash">
    <h2>Signature Pad Settings</h2>
    <div class="hyphen-signature-input-dash__header">
      <div class="text-hint">
        <span v-html="alert"></span><span>Signature Pad</span>
      </div>
      <p>
        A component for capturing signatures. Users can draw their signatures
        using the mouse or touch input on touch-enabled devices.
      </p>
    </div>

    <div class="hyphen-signature-input-dash__content">
      <hyphen-input
        v-model="editForm.label"
        id="dash-signature-label"
        placeholder="Enter label"
        label="Label"
        description="Label of the signature pad field"
      />

      <hyphen-input
        v-model="editForm.placeholder"
        id="dash-signature-placeholder"
        placeholder="Please sign here"
        label="Placeholder (optional)"
        description="Sign your name as proof of agreement"
      />

      <hyphen-input
        v-model="editForm.description"
        id="dash-signature-description"
        placeholder="Enter description text"
        label="Description (optional)"
        description="Description of the signature pad field"
      />

      <hyphen-select
        v-model="editForm.required"
        label="Required"
        placeholder="Select a type"
        description="Is this field required?"
        option-label="label"
        option-value="value"
        :options="[
          {
            label: 'Yes',
            value: 'true',
          },
          {
            label: 'No',
            value: 'false',
          },
        ]"
      />

      <!-- Additional attributes for the signature pad -->
      <!-- <hyphen-input v-model="editForm.attributes.penColor" id="dash-signature-pen-color"
                placeholder="Enter pen color" label="Pen Color" description="Color of the pen for signing" />

            <hyphen-input v-model="editForm.attributes.backgroundColor" id="dash-signature-background-color"
                placeholder="Enter background color" label="Background Color"
                description="Background color of the signature pad" />

            <hyphen-input v-model="editForm.attributes.width" id="dash-signature-width" placeholder="Enter width"
                label="Width" description="Width of the signature pad" />

            <hyphen-input v-model="editForm.attributes.height" id="dash-signature-height" placeholder="Enter height"
                label="Height" description="Height of the signature pad" /> -->
    </div>
    <div class="hyphen-signature-input-dash__action">
      <button @click="cancel" class="cancel">Cancel</button>
      <button
        @click="apply"
        v-if="canApply"
        class="apply"
        :class="{ 'apply--disabled': !canApply }"
      >
        Apply
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed, defineProps, ref, watch, defineEmits } from "vue";
import HyphenInput from "../../base/HyphenInput.vue";
import HyphenSelect from "../../base/HyphenSelect.vue";

import { alert } from "../../../svgs.js";

const props = defineProps({
  form: {
    type: Object,
    default: null,
  },
});

const editForm = ref({
  label: "",
  placeholder: "",
  description: "",
  required: false,
});

const emit = defineEmits({
  edit(val) {
    return val;
  },
  remove(val) {
    return val;
  },
  cancelEdit(val) {
    return val;
  },
});

watch(
  () => props.form,
  (val) => {
    if (val) {
      editForm.value = {
        label: val.label,
        required: val.required.toString(),
        ...val.attributes,
      };
    }
  },
  { immediate: true }
);

const canApply = computed(() => {
  return editForm.value.label;
});

const apply = () => {
  const data = {
    id: props.form.id,
    type: props.form.type,
    label: editForm.value.label,
    key: key.value, // Note: key.value is not defined in your code, you may need to adjust this
    placeholder: editForm.value.placeholder,
    description: editForm.value.description,
    required: editForm.value.required,
    attributes: {
      types: editForm.value.types,
      placeholder: editForm.value.placeholder,
      description: editForm.value.description,
      editable: props.form.attributes.editable ?? true,
    },
  };
  emit("edit", data);
};

const cancel = () => {
  if (!props.form.label) {
    emit("remove", props.form);
  } else {
    emit("cancelEdit", props.form);
  }
};

const key = computed(() => {
  // convert string to snake case
  return editForm.value.label
    .toLowerCase()
    .replace(/ /g, "_")
    .replace(/[^\w-]+/g, "");
});
</script>

<style lang="scss" scoped>
.hyphen-signature-input-dash {
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;

  h2 {
    color: var(--hyphen-Blue, #19283d);
    font-family: Inter;
    font-size: 17px;
    font-style: normal;
    font-weight: 700;
    line-height: 28px;
    /* 175% */
  }

  &__header {
    padding: 20px;
    margin: 20px 0px;
    border-radius: 4px;
    background: #f8f9fa;
    box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.03);

    .text-hint {
      display: flex;
      align-items: center;
      gap: 10px;

      & > span:nth-child(2) {
        color: #000;
        font-family: Inter;
        font-size: 16px;
        font-style: normal;
        font-weight: 600;
        line-height: 22px;
        margin-bottom: 3px;
      }
    }

    p {
      color: var(--body-text, rgba(25, 40, 61, 0.8));
      font-family: Inter;
      font-size: 15px;
      font-style: normal;
      font-weight: 400;
      line-height: 22px;
      /* 183.333% */
    }
  }

  &__content {
    flex: 1;
    overflow: auto;
    gap: 30px;
    display: flex;
    flex-direction: column;
    padding: 10px;
  }

  &__content::-webkit-scrollbar {
    width: 8px;
    border-radius: 10px;
  }

  &__content::-webkit-scrollbar-track {
    box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.1);
  }

  &__content::-webkit-scrollbar-thumb {
    background-color: var(--hyphen-Blue, #19283d);
    border-radius: 10px;
  }

  &__action {
    box-sizing: border-box;
    display: flex;
    gap: 20px;
    padding: 10px 0px;
    margin-bottom: 40px;
    z-index: 99;

    button {
      flex: 1;

      padding: 15px 30px;
      border-radius: 4px;
      border: none;

      font-size: 16px;
      font-weight: 500;
      cursor: pointer;

      &.cancel {
        border-radius: 4px;
        background: var(--secondary-button, #f4f5f6);
      }

      &.apply {
        border-radius: 4px;
        color: #fff;
        background: var(--hyphen-Blue, #19283d);
      }
    }
  }
}
</style>
